import React from 'react';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';
import { injectIntl } from '@atlassian/jira-intl';
import {
	Tooltip,
	colors,
	useDateFormatter,
	shortDateFormat,
} from '@atlassian/jira-portfolio-3-common';
import IssueLinkComp from '@atlassian/jira-portfolio-3-common/src/issue-link';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { ISSUE_STATUS_APPEARANCE } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import DropMenu from './drop-menu';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

function Issue({
	DropMenuComponent = DropMenu,
	focusIssueLink,
	incomingLinks,
	intl,
	isInFocus,
	issuesById,
	issueStatusesById,
	issueTypesById,
	itemId,
	onCardClick,
	onFilterByIssue,
	outgoingLinks,
	position,
	projectsById,
	setIssueFilter,
}: Props) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatTimestamp } = useDateFormatter();

	const issue = issuesById[itemId];
	const issueType = issueTypesById[`${issue.type}`];
	const project = projectsById[issue.project];
	const status = issueStatusesById[`${issue.status || ''}`];

	const width = 295;
	const height = 105;

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={styles.container}
			style={{
				top: position.y - height / 2,

				left: position.x - width / 2,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				width,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				height,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				backgroundColor: token('elevation.surface.raised', colors.N0),
				borderColor: isInFocus
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						token('color.border.focused', colors.B400)
					: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						token('color.border', colors.N40),
				borderWidth: isInFocus ? '2px' : '1px',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				cursor: 'pointer',
			}}
			role="button"
			onKeyDown={() => focusIssueLink(issue.id)}
			onClick={(e) => {
				e.stopPropagation();
				onCardClick(issue.id);
				focusIssueLink(issue.id);

				const [actionSubject, action] =
					PRODUCT_ANALYTICS_EVENT_NAMES.CLICKED_DEPENDENCY_CARD.split(' ');
				fireUIAnalytics(createAnalyticsEvent({ action, actionSubject }), {
					outgoingLinkedIssues: (outgoingLinks[itemId] || []).length,
					incomingLinkedIssues: (incomingLinks[itemId] || []).length,
				});
			}}
			tabIndex={0}
			data-testid={`portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.issue-card-${issue.id}`}
		>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<div className={styles.innerContent}>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={styles.headerContainer}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.header}>
						<div
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className={styles.icon}
							style={{
								backgroundImage: issueType && `url(${issueType.iconUrl})`,
							}}
						/>
						{project && (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							<div className={styles.issueLink}>
								<IssueLinkComp issueKey={issue.issueKey} projectKey={project.key} />
							</div>
						)}
					</div>
					<div
						role="button"
						tabIndex={-1}
						onClick={(e) => {
							// Prevent `dependencyCard clicked` event from being triggered
							e.stopPropagation();
						}}
						onKeyDown={(e) => {
							// Prevent `dependencyCard clicked` event from being triggered
							e.stopPropagation();
						}}
					>
						<DropMenuComponent
							issue={issue}
							setIssueFilter={setIssueFilter}
							onFilterByIssue={onFilterByIssue}
						/>
					</div>
				</div>
				<Tooltip key={issue.id} content={issue.summary}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.issueSummary}>
						<span>{issue.summary}</span>
					</div>
				</Tooltip>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={styles.bottom}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.dates}>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div className={styles.dateWrapper}>
							<div>{intl.formatMessage(messages.startDate)}</div>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<div className={styles.dateValue}>
								{isDefined(issue.baselineStart)
									? formatTimestamp(issue.baselineStart, shortDateFormat)
									: '-'}
							</div>
						</div>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div className={styles.dateWrapper}>
							<div>{intl.formatMessage(messages.endDate)}</div>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<div className={styles.dateValue}>
								{isDefined(issue.baselineEnd)
									? formatTimestamp(issue.baselineEnd, shortDateFormat)
									: '-'}
							</div>
						</div>
					</div>
					{status && (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						<div className={styles.status}>
							<Lozenge appearance={ISSUE_STATUS_APPEARANCE[status.categoryId]}>
								{status.name}
							</Lozenge>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default injectIntl(Issue);
